'use client'

import ConfirmModal from '@/components/confirm-modal'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import {
    CreateDiagnosisData,
    DIAGNOSIS_CERTAINTY_LABELS,
    DIAGNOSIS_STATUS_COLORS,
    DIAGNOSIS_STATUS_LABELS,
    DIAGNOSIS_TYPE_LABELS,
    Diagnosis,
    DiagnosisCertainty,
    DiagnosisStatus,
    DiagnosisType,
    IcdCode,
    UpdateDiagnosisData,
    createDiagnosis,
    deleteDiagnosis,
    getDiagnosesByClient,
    searchIcdCodes,
    updateDiagnosis,
} from '@/lib/api/prontuario/prontuario-api'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useEffect, useState } from 'react'
import {
    MdAdd,
    MdCheck,
    MdClose,
    MdDelete,
    MdEdit,
    MdMedicalServices,
    MdSearch,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioData } from './prontuario-context'

interface DiagnosisFormData {
    professionalId: string
    icdCodeId: number | null
    customDiagnosis: string
    type: DiagnosisType
    status: DiagnosisStatus
    certainty: DiagnosisCertainty
    diagnosisDate: string
    resolutionDate: string
    notes: string
}

const defaultFormData: DiagnosisFormData = {
    professionalId: '',
    icdCodeId: null,
    customDiagnosis: '',
    type: DiagnosisType.PRIMARY,
    status: DiagnosisStatus.DIAGNOSIS_ACTIVE,
    certainty: DiagnosisCertainty.CONFIRMED,
    diagnosisDate: format(new Date(), 'yyyy-MM-dd'),
    resolutionDate: '',
    notes: '',
}

type FilterStatus = 'all' | 'active' | 'resolved'

export default function DiagnosticosPage() {
    const { client, clientId } = useProntuarioData()
    const { user, isProfessional } = useAuth()

    const [diagnoses, setDiagnoses] = useState<Diagnosis[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [formData, setFormData] = useState<DiagnosisFormData>(defaultFormData)
    const [filterStatus, setFilterStatus] = useState<FilterStatus>('all')

    // ICD Search
    const [icdSearchQuery, setIcdSearchQuery] = useState('')
    const [icdSearchResults, setIcdSearchResults] = useState<IcdCode[]>([])
    const [selectedIcd, setSelectedIcd] = useState<IcdCode | null>(null)
    const [searchingIcd, setSearchingIcd] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [diagnosisToDelete, setDiagnosisToDelete] = useState<number | null>(
        null
    )

    useEffect(() => {
        if (clientId) {
            loadDiagnoses()
        }
    }, [clientId, user?.companyId])

    const loadDiagnoses = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            const response = await getDiagnosesByClient(parseInt(clientId, 10))
            if (response.success) {
                setDiagnoses(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar diagnósticos')
            }
        } catch (error) {
            toast.error('Erro ao carregar diagnósticos')
        } finally {
            setLoading(false)
        }
    }

    const handleIcdSearch = useCallback(async (query: string) => {
        if (query.length < 2) {
            setIcdSearchResults([])
            return
        }

        setSearchingIcd(true)
        try {
            const response = await searchIcdCodes(query)
            if (response.success) {
                setIcdSearchResults(response.result || [])
            }
        } catch (error) {
            console.error('Erro ao buscar CID:', error)
        } finally {
            setSearchingIcd(false)
        }
    }, [])

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            handleIcdSearch(icdSearchQuery)
        }, 300)

        return () => clearTimeout(timeoutId)
    }, [icdSearchQuery, handleIcdSearch])

    const selectIcd = (icd: IcdCode) => {
        setSelectedIcd(icd)
        setIcdSearchQuery('')
        setIcdSearchResults([])
        setFormData(prev => ({
            ...prev,
            icdCodeId: icd.id,
            customDiagnosis: '',
        }))
    }

    const clearSelectedIcd = () => {
        setSelectedIcd(null)
        setFormData(prev => ({ ...prev, icdCodeId: null }))
    }

    const openModal = (diagnosis?: Diagnosis) => {
        if (diagnosis) {
            setEditingId(diagnosis.id)
            setFormData({
                professionalId:
                    diagnosis.professional?.id?.toString() ||
                    diagnosis.professionalId?.toString() ||
                    '',
                icdCodeId: diagnosis.icdCodeId || null,
                customDiagnosis: diagnosis.customDiagnosis || '',
                type: diagnosis.type,
                status: diagnosis.status,
                certainty: diagnosis.certainty,
                diagnosisDate: diagnosis.diagnosisDate
                    ? diagnosis.diagnosisDate.split('T')[0]
                    : '',
                resolutionDate: diagnosis.resolutionDate
                    ? diagnosis.resolutionDate.split('T')[0]
                    : '',
                notes: diagnosis.notes || '',
            })
            if (diagnosis.icdCode) {
                setSelectedIcd(diagnosis.icdCode)
            }
        } else {
            setEditingId(null)
            setFormData(defaultFormData)
            setSelectedIcd(null)
        }
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        setEditingId(null)
        setFormData(defaultFormData)
        setSelectedIcd(null)
        setIcdSearchQuery('')
        setIcdSearchResults([])
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!isProfessional && !formData.professionalId) {
            toast.error('Selecione um profissional responsável')
            return
        }

        if (!formData.icdCodeId && !formData.customDiagnosis.trim()) {
            toast.error(
                'Selecione um CID ou informe um diagnóstico personalizado'
            )
            return
        }

        if (!clientId) return

        setSaving(true)
        try {
            if (editingId) {
                const updateData: UpdateDiagnosisData = {
                    icdCodeId: formData.icdCodeId || undefined,
                    customDiagnosis:
                        formData.customDiagnosis.trim() || undefined,
                    type: formData.type,
                    status: formData.status,
                    certainty: formData.certainty,
                    diagnosisDate: formData.diagnosisDate || undefined,
                    resolutionDate: formData.resolutionDate || undefined,
                    notes: formData.notes.trim() || undefined,
                }

                const response = await updateDiagnosis(editingId, updateData)
                if (response.success) {
                    toast.success('Diagnóstico atualizado com sucesso!')
                    await loadDiagnoses()
                    closeModal()
                } else {
                    toast.error(
                        response.error || 'Erro ao atualizar diagnóstico'
                    )
                }
            } else {
                const createData: CreateDiagnosisData = {
                    clientId: parseInt(clientId, 10),
                    companyId: user!.companyId!,
                    professionalId: isProfessional
                        ? user!.professionalId!
                        : parseInt(formData.professionalId, 10),
                    icdCodeId: formData.icdCodeId || undefined,
                    customDiagnosis:
                        formData.customDiagnosis.trim() || undefined,
                    type: formData.type,
                    status: formData.status,
                    certainty: formData.certainty,
                    diagnosisDate: formData.diagnosisDate || undefined,
                    resolutionDate: formData.resolutionDate || undefined,
                    notes: formData.notes.trim() || undefined,
                }

                const response = await createDiagnosis(createData)
                if (response.success) {
                    toast.success('Diagnóstico registrado com sucesso!')
                    await loadDiagnoses()
                    closeModal()
                } else {
                    toast.error(
                        response.error || 'Erro ao registrar diagnóstico'
                    )
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar diagnóstico')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = (id: number) => {
        setDiagnosisToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!diagnosisToDelete) return

        try {
            const response = await deleteDiagnosis(diagnosisToDelete)
            if (response.success) {
                toast.success('Diagnóstico excluído com sucesso!')
                await loadDiagnoses()
            } else {
                toast.error(response.error || 'Erro ao excluir diagnóstico')
            }
        } catch (error) {
            toast.error('Erro ao excluir diagnóstico')
        } finally {
            setShowDeleteModal(false)
            setDiagnosisToDelete(null)
        }
    }

    const filteredDiagnoses = diagnoses.filter(d => {
        if (filterStatus === 'active') {
            return (
                d.status === DiagnosisStatus.DIAGNOSIS_ACTIVE ||
                d.status === DiagnosisStatus.CHRONIC
            )
        }
        if (filterStatus === 'resolved') {
            return (
                d.status === DiagnosisStatus.DIAGNOSIS_RESOLVED ||
                d.status === DiagnosisStatus.DIAGNOSIS_IN_REMISSION
            )
        }
        return true
    })

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando diagnosticos...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h2 className="text-xl font-bold text-base-content">
                        Diagnósticos
                    </h2>
                    <p className="text-sm text-base-content/60">
                        Gerencie os diagnósticos do paciente
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Filter */}
                    <div className="join">
                        <button
                            onClick={() => setFilterStatus('all')}
                            className={`btn btn-sm join-item ${filterStatus === 'all' ? 'btn-secondary' : 'btn-ghost'}`}
                        >
                            Todos
                        </button>
                        <button
                            onClick={() => setFilterStatus('active')}
                            className={`btn btn-sm join-item ${filterStatus === 'active' ? 'btn-secondary' : 'btn-ghost'}`}
                        >
                            Ativos
                        </button>
                        <button
                            onClick={() => setFilterStatus('resolved')}
                            className={`btn btn-sm join-item ${filterStatus === 'resolved' ? 'btn-secondary' : 'btn-ghost'}`}
                        >
                            Resolvidos
                        </button>
                    </div>
                    <button
                        onClick={() => openModal()}
                        className="btn btn-secondary gap-2"
                    >
                        <MdAdd className="text-lg" />
                        Novo Diagnóstico
                    </button>
                </div>
            </div>

            {/* Diagnoses List */}
            {filteredDiagnoses.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                    <MdMedicalServices className="text-4xl mx-auto mb-3 text-base-content/20" />
                    <p className="text-base-content/60">
                        {filterStatus === 'all'
                            ? 'Nenhum diagnóstico registrado'
                            : `Nenhum diagnóstico ${filterStatus === 'active' ? 'ativo' : 'resolvido'}`}
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredDiagnoses.map(diagnosis => (
                        <div
                            key={diagnosis.id}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm overflow-hidden"
                        >
                            <div className="p-5">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="p-2 bg-secondary/10 rounded-lg">
                                                <MdMedicalServices className="text-xl text-secondary" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-base-content">
                                                    {diagnosis.icdCode
                                                        ? `${diagnosis.icdCode.code} - ${diagnosis.icdCode.description}`
                                                        : diagnosis.customDiagnosis}
                                                </h3>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-2 mb-3">
                                            <span
                                                className={`badge ${DIAGNOSIS_STATUS_COLORS[diagnosis.status]}`}
                                            >
                                                {
                                                    DIAGNOSIS_STATUS_LABELS[
                                                        diagnosis.status
                                                    ]
                                                }
                                            </span>
                                            <span className="badge badge-ghost">
                                                {
                                                    DIAGNOSIS_TYPE_LABELS[
                                                        diagnosis.type
                                                    ]
                                                }
                                            </span>
                                            <span className="badge badge-outline">
                                                {
                                                    DIAGNOSIS_CERTAINTY_LABELS[
                                                        diagnosis.certainty
                                                    ]
                                                }
                                            </span>
                                        </div>

                                        {diagnosis.notes && (
                                            <p className="text-sm text-base-content/70 mb-3">
                                                {diagnosis.notes}
                                            </p>
                                        )}

                                        <div className="flex items-center gap-4 text-xs text-base-content/50">
                                            <span>
                                                Diagnosticado:{' '}
                                                {format(
                                                    new Date(
                                                        diagnosis.diagnosisDate
                                                    ),
                                                    'dd/MM/yyyy',
                                                    { locale: ptBR }
                                                )}
                                            </span>
                                            {diagnosis.resolutionDate && (
                                                <span>
                                                    Resolução:{' '}
                                                    {format(
                                                        new Date(
                                                            diagnosis.resolutionDate
                                                        ),
                                                        'dd/MM/yyyy',
                                                        { locale: ptBR }
                                                    )}
                                                </span>
                                            )}
                                        </div>

                                        {diagnosis.professional && (
                                            <p className="text-xs text-base-content/50 mt-2">
                                                Por:{' '}
                                                {diagnosis.professional.name}
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => openModal(diagnosis)}
                                            className="btn btn-ghost btn-sm btn-square"
                                        >
                                            <MdEdit className="text-lg" />
                                        </button>
                                        <button
                                            onClick={() =>
                                                handleDelete(diagnosis.id)
                                            }
                                            className="btn btn-ghost btn-sm btn-square text-error"
                                        >
                                            <MdDelete className="text-lg" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {modalOpen && (
                <div className="modal modal-open">
                    <div className="modal-box !max-w-2xl">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold">
                                {editingId
                                    ? 'Editar Diagnóstico'
                                    : 'Novo Diagnóstico'}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="btn btn-ghost btn-sm btn-square"
                            >
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Professional Selector */}
                            {!isProfessional && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Profissional Responsável{' '}
                                            <span className="text-error">
                                                *
                                            </span>
                                        </span>
                                    </label>
                                    <ProfessionalSearchSelector
                                        value={
                                            formData.professionalId
                                                ? Number(
                                                      formData.professionalId
                                                  )
                                                : null
                                        }
                                        onChange={id =>
                                            setFormData(prev => ({
                                                ...prev,
                                                professionalId: id
                                                    ? String(id)
                                                    : '',
                                            }))
                                        }
                                        label=""
                                        placeholder="Selecione..."
                                        disabled={!!editingId}
                                    />
                                </div>
                            )}

                            {/* ICD Search */}
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Código CID
                                    </span>
                                </label>
                                {selectedIcd ? (
                                    <div className="flex items-center gap-2 p-3 bg-base-200 rounded-lg">
                                        <span className="flex-1">
                                            <strong>{selectedIcd.code}</strong>{' '}
                                            - {selectedIcd.description}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={clearSelectedIcd}
                                            className="btn btn-ghost btn-sm btn-square"
                                        >
                                            <MdClose className="text-lg" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <input
                                            type="text"
                                            className="input input-bordered w-full pr-10"
                                            placeholder="Buscar código CID..."
                                            value={icdSearchQuery}
                                            onChange={e =>
                                                setIcdSearchQuery(
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <MdSearch className="absolute z-10 right-3 top-1/2 -translate-y-1/2 text-base-content/50" />
                                        {icdSearchResults.length > 0 && (
                                            <div className="absolute z-10 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                                {icdSearchResults.map(icd => (
                                                    <button
                                                        key={icd.id}
                                                        type="button"
                                                        onClick={() =>
                                                            selectIcd(icd)
                                                        }
                                                        className="w-full px-4 py-2 text-left hover:bg-base-200 text-sm"
                                                    >
                                                        <strong>
                                                            {icd.code}
                                                        </strong>{' '}
                                                        - {icd.description}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Custom Diagnosis */}
                            {!selectedIcd && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Ou informe um diagnóstico
                                            personalizado
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Descreva o diagnóstico..."
                                        value={formData.customDiagnosis}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                customDiagnosis: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Tipo
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={formData.type}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                type: e.target
                                                    .value as DiagnosisType,
                                            }))
                                        }
                                    >
                                        {Object.entries(
                                            DIAGNOSIS_TYPE_LABELS
                                        ).map(([key, label]) => (
                                            <option key={key} value={key}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Status
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={formData.status}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                status: e.target
                                                    .value as DiagnosisStatus,
                                            }))
                                        }
                                    >
                                        {Object.entries(
                                            DIAGNOSIS_STATUS_LABELS
                                        ).map(([key, label]) => (
                                            <option key={key} value={key}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Certeza
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={formData.certainty}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                certainty: e.target
                                                    .value as DiagnosisCertainty,
                                            }))
                                        }
                                    >
                                        {Object.entries(
                                            DIAGNOSIS_CERTAINTY_LABELS
                                        ).map(([key, label]) => (
                                            <option key={key} value={key}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Data do Diagnóstico
                                        </span>
                                    </label>
                                    <input
                                        type="date"
                                        className="input input-bordered w-full"
                                        value={formData.diagnosisDate}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                diagnosisDate: e.target.value,
                                            }))
                                        }
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Data de Resolução
                                        </span>
                                    </label>
                                    <input
                                        type="date"
                                        className="input input-bordered w-full"
                                        value={formData.resolutionDate}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                resolutionDate: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Observações
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Observações adicionais..."
                                    rows={3}
                                    value={formData.notes}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            notes: e.target.value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="modal-action">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="btn btn-ghost"
                                    disabled={saving}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-secondary gap-2"
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <span className="loading loading-spinner loading-sm"></span>
                                            Salvando...
                                        </>
                                    ) : (
                                        <>
                                            <MdCheck className="text-lg" />
                                            Salvar
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                    <div className="modal-backdrop" onClick={closeModal}></div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setDiagnosisToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir diagnóstico"
                message="Tem certeza que deseja excluir este diagnóstico?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
